import { Preview } from "@msuite/printable/preview";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import { PDF_ASPECT_RATIO } from "@msuite/utils/files";
import { guardNull } from "@msuite/utils/misc";
import type { WorkspaceSchema } from "@msuite/utils/schemas";
import { generateId, guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import { PDFViewer } from "@react-pdf/renderer";
import React, { memo, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useRemoteFileUrl } from "@/hooks/use-remote-file-url";
import { useWorkspaceMediaDefault } from "./use-workspace-media-defaults";
import type { WorkspaceFormImagesProps } from "./workspace-form-images";

const PLACEHOLDER_STRING = "blank";

/** Props Interface */
interface LetterPaperPreviewProps extends WorkspaceFormImagesProps {}

export function LetterPaperPreview({
	workspaceId,
	workspaceLogoFileController,
	workspaceLetterpaperFileController,
}: LetterPaperPreviewProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<WorkspaceSchema>();
	const workspace = useWatch({ control: methods.control });
	const { workspaceLogoUrl, workspaceLetterpaperUrl } = useWorkspaceMediaDefault(workspaceId);
	const [renderId, setRenderId] = useState(generateId());

	/** Computed */
	const logoFile = workspaceLogoFileController.files.at(0);
	const logoFileRemoteUrl = useRemoteFileUrl(
		guardNull(workspaceLogoFileController.defaultFiles?.at(0)),
	);
	const letterPaperFile = workspaceLetterpaperFileController.files.at(0);
	const letterPaperFileRemoteUrl = useRemoteFileUrl(
		guardNull(workspaceLetterpaperFileController.defaultFiles?.at(0)),
	);

	const logoUrl = useMemo(() => {
		if (!logoFile) return "";
		const url = URL.createObjectURL(logoFile);
		return url;
	}, [logoFile, logoFileRemoteUrl]);

	const letterPaperUrl = useMemo(() => {
		if (!letterPaperFile) return "";
		const url = URL.createObjectURL(letterPaperFile);
		return url;
	}, [letterPaperFile, letterPaperFileRemoteUrl]);

	/** Render */
	return (
		<div
			className="w-[400px] h-full max-h-[60vh] grid gap-y-4"
			style={{ gridTemplateRows: "1fr max-content" }}
		>
			<Viewer
				workspace={workspace as any}
				logoUrl={logoUrl || workspaceLogoUrl}
				letterPaperUrl={letterPaperUrl || workspaceLetterpaperUrl}
				renderId={renderId}
			/>
			<div className="h-stack items-end">
				<Button
					size="xxs"
					onClick={() => {
						setRenderId(generateId());
					}}
				>
					{t("general.refresh")}
				</Button>
			</div>
		</div>
	);
}

/** Props Interface */
interface ViewerProps {
	workspace: WorkspaceSchema;
	logoUrl: Nullable<string>;
	letterPaperUrl: Nullable<string>;
	renderId: string;
}

const Viewer = memo(
	({ workspace, logoUrl, letterPaperUrl }: ViewerProps) => {
		/** Hooks */
		const disclosure = useDisclosure();

		/** Render */
		return (
			<React.Fragment>
				<div
					className={cn(
						"justify-center h-full w-full rounded-xl overflow-hidden",
						disclosure.isOpen && "fixed inset-0 flex-row items-center bg-black/20 z-900",
					)}
					onClick={disclosure.onToggle}
				>
					<div className={cn("h-full cursor-pointer! w-full", disclosure.isOpen && "flex-center")}>
						<PDFViewer
							key={JSON.stringify(workspace)}
							style={{
								maxHeight: "100%",
								aspectRatio: PDF_ASPECT_RATIO,
							}}
							className="pointer-events-none"
							showToolbar={false}
						>
							<Preview
								logoUrl={logoUrl}
								letterPaperUrl={letterPaperUrl}
								workspaceMetadata={{
									web: workspace.web,
									workspaceId: PLACEHOLDER_STRING,
									orgId: PLACEHOLDER_STRING,
									city: workspace.city,
									zipCode: workspace.zipCode,
									houseNumber: workspace.houseNumber ?? "",
									streetName: workspace.streetName,
									email: workspace.email,
									phone: workspace.phone,
									companyName: guardString(workspace.companyName),
									id: "_id",
									legal: workspace.legal || { blocks: [] },
									bankAccountHolder: workspace.bankAccountHolder,
									iban: workspace.iban,
									bic: workspace.bic,
									region: workspace.region ?? null,
									businessHours: workspace.businessHours ?? null,
								}}
							/>
						</PDFViewer>
					</div>
				</div>
			</React.Fragment>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.renderId === nextProps.renderId;
	},
);
